import React, { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faMinus,
  faLayerGroup,
  faSliders,
  faXmark,
  faSpinner,
  faSearch,
  faTrash,
  faCloudSunRain,
  faBars,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons'

import type {
  BoundaryShowAllLevel,
  MapStyleId,
  OmniAssetFilters,
  OmniCategory,
  OmniJobFilters,
  OmniMunicipalityFilters,
  OmniPersonFilters,
  OmniSearchHit,
} from './controlCenterMapTypes'
import { MAP_STYLES } from './mapStyles'
import { isInsideKioskVirtualKeyboard } from '../../../utils/kioskVirtualKeyboard'
import {
  OMNI_CATEGORY_ORDER,
  OMNI_GROUP_LABEL,
} from './omniSearch'
import { OmniPinnedBadge } from './OmniPinnedBadge'
import { OmniCategoryFilterFields } from './OmniCategoryFilterFields'
import { MAP_LOADING_FLAVOR } from './MapLoadingOverlay'

export function ControlCenterMapToolbar({
  mapRef,
  mapStyleId,
  setMapStyleId,
  inlineMapTools = false,
  reserveEndSpace = false,
  omniQuery,
  onOmniQueryChange,
  omniHits,
  omniBootstrapping,
  omniSearching,
  omniOpen,
  onOmniOpenChange,
  onSelectOmniHit,
  omniBadges,
  omniFocusId,
  onFocusOmniSelection,
  onRemoveOmniSelection,
  onClearOmniSelections,
  filtersOpen,
  onFiltersOpenChange,
  activeFilterTypes,
  onToggleFilterType,
  jobFilters,
  personFilters,
  assetFilters,
  municipalityFilters,
  onJobFiltersChange,
  onPersonFiltersChange,
  onAssetFiltersChange,
  onMunicipalityFiltersChange,
  jobStates,
  jobProjectTypeOptions,
  jobStatusOptions,
  assetStatusOptions,
  municipalityStates,
  showAllJobs,
  showAllPeople,
  showAllAssets,
  onShowAllJobsChange,
  onShowAllPeopleChange,
  onShowAllAssetsChange,
  boundaryShowLevel,
  onBoundaryShowLevelChange,
  boundaryOverlaysLoading,
  weatherEnabled,
  onWeatherEnabledChange,
  onUseMyLocation,
  locatingMe = false,
  myLocationActive = false,
  locateError = null,
  onClearMyLocation,
  onFocusMyLocation,
}: {
  mapRef: React.RefObject<L.Map | null>
  mapStyleId: MapStyleId
  setMapStyleId: React.Dispatch<React.SetStateAction<MapStyleId>>
  /** When false, zoom / map type / weather collapse into the hamburger menu. */
  inlineMapTools?: boolean
  /** Leave room on the right for the desktop output panel. */
  reserveEndSpace?: boolean
  omniQuery: string
  onOmniQueryChange: (next: string) => void
  omniHits: OmniSearchHit[]
  omniBootstrapping: boolean
  omniSearching: boolean
  omniOpen: boolean
  onOmniOpenChange: (open: boolean) => void
  onSelectOmniHit: (hit: OmniSearchHit) => void
  omniBadges: Array<{ id: string; category: OmniCategory; name: string; color: string }>
  omniFocusId: string | null
  onFocusOmniSelection: (id: string, category: OmniCategory) => void
  onRemoveOmniSelection: (id: string) => void
  onClearOmniSelections: () => void
  filtersOpen: boolean
  onFiltersOpenChange: (open: boolean) => void
  activeFilterTypes: OmniCategory[]
  onToggleFilterType: (category: OmniCategory) => void
  jobFilters: OmniJobFilters
  personFilters: OmniPersonFilters
  assetFilters: OmniAssetFilters
  municipalityFilters: OmniMunicipalityFilters
  onJobFiltersChange: (next: OmniJobFilters) => void
  onPersonFiltersChange: (next: OmniPersonFilters) => void
  onAssetFiltersChange: (next: OmniAssetFilters) => void
  onMunicipalityFiltersChange: (next: OmniMunicipalityFilters) => void
  jobStates: string[]
  jobProjectTypeOptions: string[]
  jobStatusOptions: string[]
  assetStatusOptions: string[]
  municipalityStates: string[]
  showAllJobs: boolean
  showAllPeople: boolean
  showAllAssets: boolean
  onShowAllJobsChange: (next: boolean) => void
  onShowAllPeopleChange: (next: boolean) => void
  onShowAllAssetsChange: (next: boolean) => void
  boundaryShowLevel: BoundaryShowAllLevel
  onBoundaryShowLevelChange: (next: BoundaryShowAllLevel) => void
  boundaryOverlaysLoading: boolean
  weatherEnabled: boolean
  onWeatherEnabledChange: (next: boolean) => void
  onUseMyLocation: () => void
  locatingMe?: boolean
  myLocationActive?: boolean
  locateError?: string | null
  onClearMyLocation?: () => void
  onFocusMyLocation?: () => void
}) {
  const cfg = MAP_STYLES[mapStyleId]
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [mapToolsOpen, setMapToolsOpen] = useState(false)
  const [loadingFlavorIdx, setLoadingFlavorIdx] = useState(0)
  const activeFilterSet = useMemo(() => new Set(activeFilterTypes), [activeFilterTypes])

  useEffect(() => {
    if (inlineMapTools) setMapToolsOpen(false)
  }, [inlineMapTools])
  const filterCounts = useMemo((): Record<OmniCategory, number> => {
    return {
      job:
        jobFilters.states.length +
        jobFilters.projectTypes.length +
        jobFilters.statuses.length +
        jobFilters.activity.length +
        (showAllJobs ? 1 : 0),
      person: personFilters.kinds.length + personFilters.clockedIn.length + (showAllPeople ? 1 : 0),
      asset: assetFilters.kinds.length + assetFilters.statuses.length + (showAllAssets ? 1 : 0),
      municipality:
        municipalityFilters.states.length +
        municipalityFilters.levels.length +
        municipalityFilters.licenseTypes.length +
        municipalityFilters.statuses.length +
        (boundaryShowLevel !== 'none' ? 1 : 0),
    }
  }, [
    jobFilters,
    personFilters,
    assetFilters,
    municipalityFilters,
    showAllJobs,
    showAllPeople,
    showAllAssets,
    boundaryShowLevel,
  ])
  const filtersConfiguredCount = useMemo(
    () =>
      filterCounts.job +
      filterCounts.person +
      filterCounts.asset +
      filterCounts.municipality,
    [filterCounts],
  )
  const zoom = (delta: number) => {
    const m = mapRef.current
    if (!m) return
    if (delta > 0) m.zoomIn(1)
    else m.zoomOut(1)
  }

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      if (isInsideKioskVirtualKeyboard(event.target)) return
      if (!wrapRef.current?.contains(event.target as Node)) {
        onOmniOpenChange(false)
        setMapToolsOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [onOmniOpenChange])

  useEffect(() => {
    if (!omniBootstrapping) {
      setLoadingFlavorIdx(0)
      return
    }
    const id = window.setInterval(() => {
      setLoadingFlavorIdx((i) => (i + 1) % MAP_LOADING_FLAVOR.length)
    }, 2200)
    return () => window.clearInterval(id)
  }, [omniBootstrapping])

  const groupedHits = useMemo(() => {
    const groups: Array<{ category: OmniCategory; label: string; items: OmniSearchHit[] }> = []
    for (const category of OMNI_CATEGORY_ORDER) {
      const items = omniHits.filter((h) => h.category === category)
      if (items.length === 0) continue
      groups.push({ category, label: OMNI_GROUP_LABEL[category], items })
    }
    return groups
  }, [omniHits])

  const showDropdown = !omniBootstrapping && omniOpen && omniQuery.trim().length >= 1
  const searchBusy = omniBootstrapping || omniSearching
  const searchPlaceholder = omniBootstrapping
    ? MAP_LOADING_FLAVOR[loadingFlavorIdx]
    : 'Search jobs, people, assets, municipalities…'

  return (
    <div
      className={`pointer-events-none absolute inset-x-0 top-0 z-20 flex px-3 pt-3 ${
        inlineMapTools || reserveEndSpace ? 'justify-start' : 'justify-center'
      }`}
    >
      <div
        ref={wrapRef}
        className={`pointer-events-auto flex w-full flex-col gap-2 ${
          reserveEndSpace ? 'max-w-[min(48rem,calc(54%-0.75rem))]' : 'max-w-3xl'
        }`}
      >
        <div className="flex items-start gap-2">
          <div className="relative min-w-0 flex-1">
            <div className="rounded-xl border-2 border-base-300 bg-base-100 shadow-lg">
              <div className="flex h-10 items-center gap-2 px-3">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="shrink-0 text-xs text-base-content/45 sm:text-sm"
                />
                <input
                  type="text"
                  className={`min-w-0 grow bg-transparent text-xs outline-none sm:text-sm ${
                    omniBootstrapping ? 'cursor-wait text-base-content/50 placeholder:text-base-content/45' : ''
                  }`}
                  placeholder={searchPlaceholder}
                  value={omniBootstrapping ? '' : omniQuery}
                  disabled={omniBootstrapping}
                  aria-busy={searchBusy}
                  onChange={(e) => {
                    if (omniBootstrapping) return
                    onOmniQueryChange(e.target.value)
                    onOmniOpenChange(true)
                  }}
                  onFocus={() => {
                    if (omniBootstrapping) return
                    onOmniOpenChange(true)
                    setMapToolsOpen(false)
                  }}
                  aria-label="Omni search"
                  autoComplete="off"
                  spellCheck={false}
                />
                {searchBusy ? (
                  <FontAwesomeIcon icon={faSpinner} className="shrink-0 animate-spin text-xs opacity-60" />
                ) : omniQuery ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-circle shrink-0"
                    aria-label="Clear search"
                    onClick={() => {
                      onOmniQueryChange('')
                      onOmniOpenChange(false)
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} className="text-xs" />
                  </button>
                ) : null}
                <div
                  className="tooltip tooltip-bottom shrink-0"
                  data-tip={filtersOpen ? 'Hide search filters' : 'Show search filters'}
                >
                  <button
                    type="button"
                    className={`relative btn btn-ghost btn-xs btn-circle ${
                      filtersOpen ? 'text-primary' : 'text-base-content/55'
                    }`}
                    aria-label={
                      filtersConfiguredCount > 0
                        ? filtersOpen
                          ? `Hide search filters, ${filtersConfiguredCount} filter${filtersConfiguredCount === 1 ? '' : 's'} active`
                          : `Show search filters, ${filtersConfiguredCount} filter${filtersConfiguredCount === 1 ? '' : 's'} active`
                        : filtersOpen
                          ? 'Hide search filters'
                          : 'Show search filters'
                    }
                    aria-pressed={filtersOpen}
                    onClick={() => onFiltersOpenChange(!filtersOpen)}
                  >
                    <FontAwesomeIcon icon={faSliders} className="text-xs sm:text-sm" />
                    {filtersConfiguredCount > 0 ? (
                      <span className="absolute -right-0.5 -top-0.5 badge badge-primary badge-xs h-3.5 min-h-0 w-3.5 justify-center p-0 text-[9px] font-semibold leading-none">
                        {filtersConfiguredCount}
                      </span>
                    ) : null}
                  </button>
                </div>
                {!inlineMapTools ? (
                  <>
                    <div className="mx-0.5 h-5 w-px shrink-0 bg-base-300" aria-hidden />
                    <div className="flex shrink-0 items-center gap-0.5">
                      <div
                        className="tooltip tooltip-bottom"
                        data-tip={
                          locateError
                            ? locateError
                            : myLocationActive
                              ? 'Refresh my location'
                              : 'Use my location'
                        }
                      >
                        <button
                          type="button"
                          className={`btn btn-ghost btn-xs btn-circle ${
                            myLocationActive || locatingMe
                              ? 'text-primary'
                              : locateError
                                ? 'text-error'
                                : 'text-base-content/55'
                          }`}
                          aria-label="Use my location"
                          aria-pressed={myLocationActive}
                          aria-describedby={locateError ? 'cc-locate-error' : undefined}
                          disabled={locatingMe}
                          onClick={onUseMyLocation}
                        >
                          {locatingMe ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs sm:text-sm" />
                          ) : (
                            <FontAwesomeIcon icon={faLocationDot} className="text-xs sm:text-sm" />
                          )}
                        </button>
                      </div>
                      <button
                        type="button"
                        className={`btn btn-ghost btn-xs btn-circle ${
                          mapToolsOpen || weatherEnabled ? 'text-primary' : 'text-base-content/55'
                        }`}
                        aria-label={mapToolsOpen ? 'Hide map tools' : 'Show map tools'}
                        aria-expanded={mapToolsOpen}
                        aria-pressed={mapToolsOpen}
                        onClick={() => {
                          setMapToolsOpen((v) => !v)
                          onOmniOpenChange(false)
                        }}
                      >
                        <FontAwesomeIcon icon={mapToolsOpen ? faXmark : faBars} className="text-xs sm:text-sm" />
                      </button>
                    </div>
                  </>
                ) : null}
              </div>

              {filtersOpen ? (
                <div className="space-y-2 border-t border-base-300 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-base-content/60">
                      Filters
                    </p>
                    <div className="tabs tabs-lift tabs-xs min-w-0 flex-1">
                      {OMNI_CATEGORY_ORDER.map((category) => {
                        const active = activeFilterSet.has(category)
                        const filterCount = filterCounts[category]
                        const label =
                          filterCount > 0
                            ? `${OMNI_GROUP_LABEL[category]} (${filterCount})`
                            : OMNI_GROUP_LABEL[category]
                        return (
                          <input
                            key={category}
                            type="radio"
                            name="cc-omni-filter-tabs"
                            className="tab"
                            aria-label={label}
                            checked={active}
                            onChange={() => onToggleFilterType(category)}
                          />
                        )
                      })}
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-circle h-6 min-h-0 w-6 shrink-0 text-base-content/55"
                      aria-label="Clear all filters"
                      title="Clear all filters"
                      disabled={filtersConfiguredCount === 0}
                      onClick={() => {
                        onJobFiltersChange({
                          states: [],
                          projectTypes: [],
                          statuses: [],
                          activity: [],
                        })
                        onPersonFiltersChange({ kinds: [], clockedIn: [] })
                        onAssetFiltersChange({ kinds: [], statuses: [] })
                        onMunicipalityFiltersChange({
                          states: [],
                          levels: [],
                          licenseTypes: [],
                          statuses: [],
                        })
                        onShowAllJobsChange(false)
                        onShowAllPeopleChange(false)
                        onShowAllAssetsChange(false)
                        onBoundaryShowLevelChange('none')
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-[11px]" />
                    </button>
                  </div>

                  {OMNI_CATEGORY_ORDER.map((category) =>
                    activeFilterSet.has(category) ? (
                      <OmniCategoryFilterFields
                        key={category}
                        category={category}
                        jobFilters={jobFilters}
                        personFilters={personFilters}
                        assetFilters={assetFilters}
                        municipalityFilters={municipalityFilters}
                        onJobFiltersChange={onJobFiltersChange}
                        onPersonFiltersChange={onPersonFiltersChange}
                        onAssetFiltersChange={onAssetFiltersChange}
                        onMunicipalityFiltersChange={onMunicipalityFiltersChange}
                        jobStates={jobStates}
                        jobProjectTypeOptions={jobProjectTypeOptions}
                        jobStatusOptions={jobStatusOptions}
                        assetStatusOptions={assetStatusOptions}
                        municipalityStates={municipalityStates}
                        showAllJobs={showAllJobs}
                        showAllPeople={showAllPeople}
                        showAllAssets={showAllAssets}
                        onShowAllJobsChange={onShowAllJobsChange}
                        onShowAllPeopleChange={onShowAllPeopleChange}
                        onShowAllAssetsChange={onShowAllAssetsChange}
                        boundaryShowLevel={boundaryShowLevel}
                        onBoundaryShowLevelChange={onBoundaryShowLevelChange}
                        boundaryOverlaysLoading={boundaryOverlaysLoading}
                      />
                    ) : null,
                  )}
                </div>
              ) : null}
            </div>

            {showDropdown ? (
              <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto rounded-xl border-2 border-base-300 bg-base-100 p-1 shadow-lg">
                {groupedHits.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-base-content/55">No matches</p>
                ) : (
                  groupedHits.map((group) => (
                    <div key={group.category} className="mb-1 last:mb-0">
                      <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                        {group.label}
                      </p>
                      <ul className="space-y-0.5">
                        {group.items.map((hit) => (
                          <li key={`${hit.category}:${hit.id}`}>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-base-200"
                              onClick={() => onSelectOmniHit(hit)}
                            >
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: hit.color }}
                                aria-hidden
                              />
                              <span className="min-w-0 flex-1">
                                <span className="flex min-w-0 items-center gap-1.5">
                                  <span className="block truncate font-medium leading-tight">
                                    {hit.name}
                                  </span>
                                  {hit.isExternal ? (
                                    <span className="badge badge-error badge-xs shrink-0">
                                      Not in DB
                                    </span>
                                  ) : null}
                                </span>
                                {hit.detail ? (
                                  <span className="mt-0.5 block truncate text-[11px] leading-snug text-base-content/55">
                                    {hit.detail}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            ) : null}

            {!inlineMapTools && mapToolsOpen ? (
              <div className="absolute top-full right-0 z-20 mt-1 flex w-auto flex-col items-stretch gap-1 rounded-xl border-2 border-base-300 bg-base-100 p-1 shadow-lg">
                <div className="tooltip tooltip-left" data-tip="Zoom in">
                  <button
                    type="button"
                    className="btn btn-ghost btn-square btn-sm"
                    aria-label="Zoom in"
                    onClick={() => zoom(1)}
                  >
                    <FontAwesomeIcon icon={faPlus} className="text-sm" />
                  </button>
                </div>
                <div className="tooltip tooltip-left" data-tip="Zoom out">
                  <button
                    type="button"
                    className="btn btn-ghost btn-square btn-sm"
                    aria-label="Zoom out"
                    onClick={() => zoom(-1)}
                  >
                    <FontAwesomeIcon icon={faMinus} className="text-sm" />
                  </button>
                </div>
                <div className="mx-1 h-px bg-base-300" aria-hidden />
                <div className="dropdown dropdown-end">
                  <div className="tooltip tooltip-left" data-tip={`Map type: ${cfg.label}`}>
                    <button
                      type="button"
                      tabIndex={0}
                      className="btn btn-ghost btn-square btn-sm"
                      aria-label={`Map type: ${cfg.label}`}
                      aria-haspopup="listbox"
                    >
                      <FontAwesomeIcon icon={faLayerGroup} className="text-sm opacity-80" />
                    </button>
                  </div>
                  <ul
                    tabIndex={0}
                    className="dropdown-content menu z-30 mt-1 w-40 rounded-xl border-2 border-base-300 bg-base-100 p-1 shadow-lg"
                    role="listbox"
                  >
                    {(Object.keys(MAP_STYLES) as MapStyleId[]).map((id) => (
                      <li key={id} role="option" aria-selected={id === mapStyleId}>
                        <button
                          type="button"
                          className={`text-sm ${id === mapStyleId ? 'active font-semibold' : ''}`}
                          onPointerDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setMapStyleId(id)
                          }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setMapStyleId(id)
                          }}
                        >
                          {MAP_STYLES[id].label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mx-1 h-px bg-base-300" aria-hidden />
                <div
                  className="tooltip tooltip-left"
                  data-tip={weatherEnabled ? 'Hide weather radar' : 'Show weather radar'}
                >
                  <button
                    type="button"
                    className={`btn btn-ghost btn-square btn-sm ${
                      weatherEnabled ? 'btn-active text-primary' : ''
                    }`}
                    aria-label={weatherEnabled ? 'Hide weather radar' : 'Show weather radar'}
                    aria-pressed={weatherEnabled}
                    onClick={() => onWeatherEnabledChange(!weatherEnabled)}
                  >
                    <FontAwesomeIcon icon={faCloudSunRain} className="text-sm" />
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {inlineMapTools ? (
          <div
            className="tooltip tooltip-bottom shrink-0"
            data-tip={
              locateError
                ? locateError
                : myLocationActive
                  ? 'Refresh my location'
                  : 'Use my location'
            }
          >
            <button
              type="button"
              className={`btn btn-square h-11 w-11 min-h-0 rounded-xl border-2 border-base-300 bg-base-100 p-0 shadow-lg ${
                myLocationActive || locatingMe
                  ? 'text-primary'
                  : locateError
                    ? 'border-error/50 text-error'
                    : 'btn-ghost'
              }`}
              aria-label="Use my location"
              aria-pressed={myLocationActive}
              aria-describedby={locateError ? 'cc-locate-error' : undefined}
              disabled={locatingMe}
              onClick={onUseMyLocation}
            >
              {locatingMe ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm" />
              ) : (
                <FontAwesomeIcon icon={faLocationDot} className="text-sm" />
              )}
            </button>
          </div>
          ) : null}

          {/* Wide desktop: keep zoom / view / weather inline. */}
          {inlineMapTools ? (
          <div className="flex h-11 shrink-0 items-center gap-0.5 rounded-xl border-2 border-base-300 bg-base-100 p-0.5 shadow-lg">
            <div className="tooltip tooltip-bottom h-full" data-tip="Zoom in">
              <button
                type="button"
                className="btn btn-ghost btn-square h-full min-h-0 w-9"
                aria-label="Zoom in"
                onClick={() => zoom(1)}
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
              </button>
            </div>
            <div className="tooltip tooltip-bottom h-full" data-tip="Zoom out">
              <button
                type="button"
                className="btn btn-ghost btn-square h-full min-h-0 w-9"
                aria-label="Zoom out"
                onClick={() => zoom(-1)}
              >
                <FontAwesomeIcon icon={faMinus} className="text-sm" />
              </button>
            </div>
            <div className="mx-0.5 h-6 w-px shrink-0 bg-base-300" aria-hidden />
            <div className="dropdown dropdown-end h-full">
              <div
                className="tooltip tooltip-bottom h-full"
                data-tip={`Map type: ${cfg.label}`}
              >
                <button
                  type="button"
                  tabIndex={0}
                  className="btn btn-ghost h-full min-h-0 gap-1 px-2 normal-case"
                  aria-label="Map type"
                  aria-haspopup="listbox"
                >
                  <FontAwesomeIcon icon={faLayerGroup} className="text-sm opacity-80" />
                  <span className="max-w-[6rem] truncate text-xs font-medium">{cfg.label}</span>
                </button>
              </div>
              <ul
                tabIndex={0}
                className="dropdown-content menu z-20 mt-1 w-40 rounded-xl border-2 border-base-300 bg-base-100 p-1 shadow-lg"
                role="listbox"
              >
                {(Object.keys(MAP_STYLES) as MapStyleId[]).map((id) => (
                  <li key={id} role="option" aria-selected={id === mapStyleId}>
                    <button
                      type="button"
                      className={`text-sm ${id === mapStyleId ? 'active font-semibold' : ''}`}
                      onPointerDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setMapStyleId(id)
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setMapStyleId(id)
                      }}
                    >
                      {MAP_STYLES[id].label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mx-0.5 h-6 w-px shrink-0 bg-base-300" aria-hidden />
            <div
              className="tooltip tooltip-bottom h-full"
              data-tip={weatherEnabled ? 'Hide weather radar' : 'Show weather radar'}
            >
              <button
                type="button"
                className={`btn btn-ghost btn-square h-full min-h-0 w-9 ${
                  weatherEnabled ? 'btn-active text-primary' : ''
                }`}
                aria-label={weatherEnabled ? 'Hide weather radar' : 'Show weather radar'}
                aria-pressed={weatherEnabled}
                onClick={() => onWeatherEnabledChange(!weatherEnabled)}
              >
                <FontAwesomeIcon icon={faCloudSunRain} className="text-sm" />
              </button>
            </div>
          </div>
          ) : null}
        </div>

        {locateError ? (
          <p
            id="cc-locate-error"
            role="alert"
            className="rounded-lg border border-error/40 bg-error/10 px-2.5 py-1.5 text-xs text-error"
          >
            {locateError}
          </p>
        ) : null}

        {omniBadges.length > 0 || myLocationActive ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {omniBadges.length > 0 ? (
              <div  className="tooltip tooltip-bottom" data-tip="Clear all pinned entities">
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-circle h-7 min-h-0 w-7 shrink-0 border-2 border-base-300 bg-base-100 shadow-md"
                  aria-label="Clear all pinned entities"
                  onClick={onClearOmniSelections}
                >
                  <FontAwesomeIcon icon={faTrash} className="text-[11px]" />
                </button>
              </div>
            ) : null}
            {myLocationActive && onClearMyLocation ? (
              <div
                className={`inline-flex max-w-full items-center gap-1 rounded-full border-2 bg-base-100 py-0.5 pr-0.5 pl-2 shadow-md ${
                  myLocationActive ? 'border-sky-500/70' : 'border-base-300'
                }`}
              >
                <div  className="tooltip tooltip-bottom" data-tip="Focus my location">
                <button
                  type="button"
                  className="min-w-0 truncate text-left text-xs font-semibold leading-none text-base-content"
                  onClick={() => (onFocusMyLocation ?? onUseMyLocation)()}
                >
                  <FontAwesomeIcon icon={faLocationDot} className="mr-1 text-sky-600" />
                  My location
                </button>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-circle h-6 min-h-0 w-6 shrink-0"
                  aria-label="Clear my location"
                  title="Clear my location"
                  onClick={onClearMyLocation}
                >
                  <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
                </button>
              </div>
            ) : null}
            {omniBadges.map((badge) => (
              <OmniPinnedBadge
                key={`${badge.category}:${badge.id}`}
                badge={badge}
                focused={omniFocusId === badge.id}
                onFocus={onFocusOmniSelection}
                onRemove={onRemoveOmniSelection}
                mapRef={mapRef}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
